import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  Checkbox,
  Collapse,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Assignment as AssignmentIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from '@mui/icons-material';
import { getWorkItems, createWorkItem, updateWorkItem, deleteWorkItem, getUsers } from '../services/api';
import { format, parseISO, isPast, isToday } from 'date-fns';
import VoiceTranscribe from './VoiceTranscribe';

const PRIORITY_CONFIG = {
  low:    { label: 'Low',    color: '#616161', bg: '#f5f5f5' },
  normal: { label: 'Normal', color: '#1565c0', bg: '#e3f2fd' },
  high:   { label: 'High',   color: '#e65100', bg: '#fff3e0' },
  urgent: { label: 'Urgent', color: '#c62828', bg: '#ffebee' },
};

const STATUS_CONFIG = {
  pending:     { label: 'Pending',     color: '#616161' },
  in_progress: { label: 'In Progress', color: '#1565c0' },
  completed:   { label: 'Completed',   color: '#2e7d32' },
  blocked:     { label: 'Blocked',     color: '#c62828' },
};

function formatDueDate(dateStr) {
  if (!dateStr) return null;
  try {
    const d = parseISO(dateStr.slice(0, 10) + 'T00:00:00');
    if (isToday(d)) return { label: 'Today', color: '#e65100' };
    if (isPast(d)) return { label: `Due ${format(d, 'MMM d')}`, color: '#c62828' };
    return { label: format(d, 'MMM d'), color: '#616161' };
  } catch {
    return { label: dateStr, color: '#616161' };
  }
}

const STATUS_ORDER = ['in_progress', 'pending', 'blocked', 'completed'];

export default function WorkItems({ projectId }) {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [completedExpanded, setCompletedExpanded] = useState(false);

  // Filters
  const [filterStatus, setFilterStatus] = useState('active'); // 'all' | 'active' | specific status
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');

  // Form
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formAssignedTo, setFormAssignedTo] = useState('');
  const [formDueDate, setFormDueDate] = useState('');
  const [formPriority, setFormPriority] = useState('normal');

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getWorkItems(projectId);
      setItems(res.data);
    } catch (err) {
      setError('Failed to load work items.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadItems();
    getUsers()
      .then((res) => setUsers(res.data || []))
      .catch(() => {});
  }, [loadItems]);

  const handleOpenDialog = () => {
    setFormTitle('');
    setFormDescription('');
    setFormAssignedTo('');
    setFormDueDate('');
    setFormPriority('normal');
    setSaveError(null);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formTitle.trim()) {
      setSaveError('Title is required.');
      return;
    }
    try {
      setSaving(true);
      setSaveError(null);
      await createWorkItem(projectId, {
        title: formTitle.trim(),
        description: formDescription.trim() || null,
        assigned_to: formAssignedTo || null,
        due_date: formDueDate || null,
        priority: formPriority,
      });
      setDialogOpen(false);
      await loadItems();
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Failed to save work item.');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusToggle = async (item) => {
    const newStatus = item.status === 'completed' ? 'pending' : 'completed';
    try {
      const res = await updateWorkItem(projectId, item.id, { status: newStatus });
      setItems((prev) => prev.map((i) => (i.id === item.id ? res.data : i)));
    } catch (err) {
      console.error('Status toggle error:', err);
    }
  };

  const handleStatusChange = async (item, newStatus) => {
    try {
      const res = await updateWorkItem(projectId, item.id, { status: newStatus });
      setItems((prev) => prev.map((i) => (i.id === item.id ? res.data : i)));
    } catch (err) {
      console.error('Status change error:', err);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await deleteWorkItem(projectId, item.id);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  // Apply filters
  const filteredItems = items.filter((item) => {
    if (filterStatus === 'active' && item.status === 'completed') return false;
    if (filterStatus !== 'all' && filterStatus !== 'active' && item.status !== filterStatus) return false;
    if (filterAssignee !== 'all' && String(item.assigned_to) !== filterAssignee) return false;
    if (filterPriority !== 'all' && item.priority !== filterPriority) return false;
    return true;
  });

  // Group by status
  const grouped = STATUS_ORDER.reduce((acc, status) => {
    acc[status] = filteredItems.filter((i) => i.status === status);
    return acc;
  }, {});

  const completedCount = grouped.completed?.length || 0;

  // Unique assignees from current items for filter dropdown
  const assigneeOptions = [...new Map(
    items.filter((i) => i.assigned_to).map((i) => [i.assigned_to, i.assigned_to_name])
  ).entries()].map(([id, name]) => ({ id, name }));

  const renderItem = (item) => {
    const priConfig = PRIORITY_CONFIG[item.priority] || PRIORITY_CONFIG.normal;
    const dueDateInfo = formatDueDate(item.due_date);
    const isCompleted = item.status === 'completed';

    return (
      <Card
        key={item.id}
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          opacity: isCompleted ? 0.7 : 1,
          mb: 1,
        }}
      >
        <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            {/* Checkbox */}
            <Checkbox
              checked={isCompleted}
              onChange={() => handleStatusToggle(item)}
              icon={<RadioButtonUncheckedIcon />}
              checkedIcon={<CheckCircleIcon />}
              sx={{ mt: -0.5, p: 0.5, color: isCompleted ? 'success.main' : 'action.disabled' }}
              size="small"
            />

            {/* Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{
                  textDecoration: isCompleted ? 'line-through' : 'none',
                  color: isCompleted ? 'text.secondary' : 'text.primary',
                  lineHeight: 1.4,
                  wordBreak: 'break-word',
                }}
              >
                {item.title}
              </Typography>

              {/* Meta row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                {item.assigned_to_name && (
                  <Typography variant="caption" color="text.secondary">
                    👤 {item.assigned_to_name}
                  </Typography>
                )}
                {dueDateInfo && (
                  <Typography variant="caption" sx={{ color: isCompleted ? 'text.secondary' : dueDateInfo.color }}>
                    📅 {dueDateInfo.label}
                  </Typography>
                )}
                <Chip
                  label={priConfig.label}
                  size="small"
                  sx={{
                    height: 16,
                    fontSize: '0.6rem',
                    fontWeight: 'bold',
                    bgcolor: priConfig.bg,
                    color: priConfig.color,
                  }}
                />
                {/* Status chip (non-completed only) */}
                {!isCompleted && item.status !== 'pending' && (
                  <Chip
                    label={STATUS_CONFIG[item.status]?.label || item.status}
                    size="small"
                    sx={{
                      height: 16,
                      fontSize: '0.6rem',
                      color: STATUS_CONFIG[item.status]?.color || '#616161',
                      bgcolor: 'transparent',
                      border: `1px solid ${STATUS_CONFIG[item.status]?.color || '#616161'}`,
                    }}
                  />
                )}
                {item.assigned_by_name && (
                  <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
                    by {item.assigned_by_name}
                  </Typography>
                )}
              </Box>

              {item.description && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.4 }}>
                  {item.description}
                </Typography>
              )}
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 0.5 }}>
              {!isCompleted && (
                <FormControl size="small" variant="standard" sx={{ minWidth: 90 }}>
                  <Select
                    value={item.status}
                    onChange={(e) => handleStatusChange(item, e.target.value)}
                    disableUnderline
                    sx={{ fontSize: '0.72rem', color: STATUS_CONFIG[item.status]?.color }}
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="blocked">Blocked</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                  </Select>
                </FormControl>
              )}
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => handleDelete(item)} sx={{ color: 'error.light' }}>
                  <DeleteIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentIcon fontSize="small" />
          Work Items
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          size="small"
          sx={{ textTransform: 'none' }}
        >
          Add Item
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="blocked">Blocked</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
          </Select>
        </FormControl>

        {assigneeOptions.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Assignee</InputLabel>
            <Select value={filterAssignee} label="Assignee" onChange={(e) => setFilterAssignee(e.target.value)}>
              <MenuItem value="all">All Techs</MenuItem>
              {assigneeOptions.map(({ id, name }) => (
                <MenuItem key={id} value={String(id)}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Priority</InputLabel>
          <Select value={filterPriority} label="Priority" onChange={(e) => setFilterPriority(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
              <MenuItem key={key} value={key}>{cfg.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && !error && filteredItems.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          <AssignmentIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
          <Typography>No work items yet.</Typography>
          <Typography variant="body2">Click "Add Item" to assign tasks to technicians.</Typography>
        </Box>
      )}

      {/* Active items grouped by status */}
      {!loading && STATUS_ORDER.filter((s) => s !== 'completed').map((status) => {
        const group = grouped[status] || [];
        if (group.length === 0) return null;
        return (
          <Box key={status} sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography
                variant="caption"
                fontWeight="bold"
                sx={{
                  color: STATUS_CONFIG[status]?.color || 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                {STATUS_CONFIG[status]?.label} ({group.length})
              </Typography>
              <Divider sx={{ flex: 1 }} />
            </Box>
            {group.map(renderItem)}
          </Box>
        );
      })}

      {/* Completed section — collapsed by default */}
      {!loading && completedCount > 0 && (
        <Box>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, cursor: 'pointer' }}
            onClick={() => setCompletedExpanded((v) => !v)}
          >
            <Typography
              variant="caption"
              fontWeight="bold"
              sx={{ color: 'success.main', textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              Completed ({completedCount})
            </Typography>
            <Divider sx={{ flex: 1 }} />
            {completedExpanded ? <ExpandLessIcon fontSize="small" color="action" /> : <ExpandMoreIcon fontSize="small" color="action" />}
          </Box>
          <Collapse in={completedExpanded}>
            {(grouped.completed || []).map(renderItem)}
          </Collapse>
        </Box>
      )}

      {/* Add Work Item Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Work Item</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

          <TextField
            label="Title *"
            fullWidth
            value={formTitle}
            onChange={(e) => setFormTitle(e.target.value)}
            placeholder="What needs to be done?"
            sx={{ mb: 2 }}
            size="small"
          />

          <TextField
            label="Description (optional)"
            multiline
            rows={2}
            fullWidth
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            sx={{ mb: 1 }}
            size="small"
          />
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <VoiceTranscribe
              projectId={projectId}
              label="🎙️ Dictate"
              onTranscribed={(text) => {
                if (!formDescription) setFormDescription(text);
                else setFormDescription(prev => prev + '\n' + text);
              }}
            />
          </Box>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Assign To</InputLabel>
            <Select
              value={formAssignedTo}
              label="Assign To"
              onChange={(e) => setFormAssignedTo(e.target.value)}
            >
              <MenuItem value="">Unassigned</MenuItem>
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>{u.name} — {u.role}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Due Date"
              type="date"
              value={formDueDate}
              onChange={(e) => setFormDueDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              size="small"
              sx={{ flex: 1 }}
            />

            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formPriority}
                label="Priority"
                onChange={(e) => setFormPriority(e.target.value)}
              >
                {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
                  <MenuItem key={key} value={key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: cfg.color }} />
                      {cfg.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving || !formTitle.trim()}
          >
            {saving ? <CircularProgress size={20} /> : 'Add Item'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
